// ABOUTME: Modal for snoozing orders with multiple snooze options
// ABOUTME: Provides options to snooze for 4 hours, next working day, specific working day, or custom date/time
import { Box, useTheme } from "@mui/material"
import SHModal from "../../theme/Modal/SHModal"
import Shbutton from "../../theme/buttons/Shbutton"
import { tokens } from "../../../theme";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import { useState } from "react";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import { useTranslation } from "react-i18next";
import { InputDropdown } from "../../theme/dropdowns/InputDropdown";
import ShDateRangePicker from "../../theme/ShDateRangePicker";
import { addHours, addDays, format } from "date-fns";

const MSnoozeOrder = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const {t} = useTranslation();

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const [selectedOption, setSelectedOption] = useState(-1);
    const [selectedWorkingDay, setSelectedWorkingDay] = useState(-1);
    const [customDateTime, setCustomDateTime] = useState(null);

    const snoozeOptions = [
        ...(!props.isRelation ? [{ id: 0, name: t("snooze_4_hours") }] : []),
        { id: 1, name: t("snooze_next_working_day") },
        { id: 2, name: t("snooze_until_working_day") },
        { id: 3, name: t("snooze_custom_date") }
    ];

    const workingDays = [
        { id: 0, name: t("monday") },
        { id: 1, name: t("tuesday") },
        { id: 2, name: t("wednesday") },
        { id: 3, name: t("thursday") },
        { id: 4, name: t("friday") },
        ...(!props.isRelation ? [{ id: 5, name: t("saturday") }] : []),
        ...(!props.isRelation ? [{ id: 6, name: t("sunday") }] : []),
    ];

    const getNextDay = () => {
        const now = new Date();
        let nextDay = addDays(now, 1);

        // Set to 04:00 UTC
        nextDay.setUTCHours(4, 0, 0, 0);

        return nextDay;
    };

    const selectDateValue = (val) => {
        val = new Date(`${val}T04:00:00.000Z`);
        setCustomDateTime(val);
    }

    const getNextOccurrenceOfDay = (targetDayIndex) => {
        const now = new Date();
        let daysToAdd = (targetDayIndex + 1 - now.getDay() + 7) % 7;
        if (daysToAdd === 0) daysToAdd = 7; // If today is the target day, get next week

        let futureDate = addDays(now, daysToAdd);

        // Set to 04:00 UTC
        futureDate.setUTCHours(4, 0, 0, 0);

        return futureDate;
    };

    const confirm = () => {
        let snoozeUntil = null;

        switch(selectedOption) {
            case 0: // 4 hours
                snoozeUntil = addHours(new Date(), 4);
                break;
            case 1: // Next working day
                snoozeUntil = getNextDay();
                break;
            case 2: // Specific working day
                if (selectedWorkingDay !== -1) {
                    snoozeUntil = getNextOccurrenceOfDay(selectedWorkingDay);
                }
                break;
            case 3: // Custom date/time
                snoozeUntil = customDateTime;
                break;
            default:
                return;
        }

        if (snoozeUntil) {
            //const formattedDate = format(, "yyyy-MM-dd HH:mm:ss");
            props.confirm(snoozeUntil);
            resetAndClose();
        }
    };

    const resetAndClose = () => {
        setSelectedOption(-1);
        setSelectedWorkingDay(-1);
        setCustomDateTime(null);
        props.handleClose();
    };

    const isConfirmDisabled = () => {
        if (selectedOption === -1) return true;
        if (selectedOption === 2 && selectedWorkingDay === -1) return true;
        if (selectedOption === 3 && !customDateTime) return true;
        return false;
    };

    return(
        <SHModal
            open={props.open}
            onClose={resetAndClose}
        >
            <Box
                sx={{
                    maxWidth: 500,
                }}
            >
                <Box
                    sx={{
                        textAlign: "center",
                        paddingY: 4,
                        paddingX: 5,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                    }}>
                    <Subheading>{t("snooze_order")}</Subheading>
                </Box>

                <Box sx={{
                    pt: 3,
                    paddingLeft: 3,
                    paddingRight: 3,
                    paddingBottom: 3,
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}>
                    <Box>
                        <Text light>{t("snooze_option")}</Text>
                    </Box>
                    <InputDropdown
                        options={snoozeOptions}
                        selected={selectedOption}
                        onChange={(value) => {
                            setSelectedOption(value);
                            setSelectedWorkingDay(-1);
                            setCustomDateTime(null);
                        }}
                        displayName="name"
                        valueKey="id"
                        noSelection={t("choose")}
                        closeOnSelection
                    />

                    {selectedOption === 2 && (
                        <>
                            <Box sx={{ mt: 2 }}>
                                <Text light>{t("select_working_day")}</Text>
                            </Box>
                            <InputDropdown
                                options={workingDays}
                                selected={selectedWorkingDay}
                                onChange={setSelectedWorkingDay}
                                displayName="name"
                                valueKey="id"
                                noSelection={t("choose")}
                                closeOnSelection
                            />
                        </>
                    )}

                    {selectedOption === 3 && (
                        <Box sx={{ mt: 2 }}>
                            <Text light>{t("select_date_time")}</Text>
                            <Box sx={{ mt: 1 }}>
                                <ShDateRangePicker
                                    explicit={true}
                                    singleSelect={true}
                                    startDate={customDateTime}
                                    select={(value) => selectDateValue(value[0].formattedStartDate)}
                                    size={32}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>

                <Box
                    component="footer"
                    sx={{
                        display: "flex",
                        width: 1,
                        paddingBottom: isStandAlone ? "32px" : "16px",
                        alignItems: "center",
                        paddingRight: 3,
                        zIndex: 1058,
                        position: "relative",
                        paddingTop: 2,
                        background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                        borderBottomLeftRadius: 24,
                        borderBottomRightRadius: 24,
                        justifyContent: "space-between",
                        "@media screen and (max-width: 47.9375em)": {
                            paddingRight: 0,
                        }
                    }}
                >
                    <Shbutton
                        isCancel={true}
                        className={"always-visible"}
                        onClick={resetAndClose}
                        color="primary"
                        variant="text"
                        startIcon={<ArrowRightAltIcon/>}
                    >
                        {t("cancel")}
                    </Shbutton>
                    <ButtonGroup version={2}>
                        <Shbutton
                            className={"group-btn"}
                            color={theme.palette.mode === "dark" ? "blue" : "primary"}
                            onClick={confirm}
                            variant="contained"
                            disabled={isConfirmDisabled()}
                        >
                            {t("confirm")}
                        </Shbutton>
                    </ButtonGroup>
                </Box>
            </Box>
        </SHModal>
    )
}

export default MSnoozeOrder;
